/**
 * The end: a code and the way back to Prolific.
 *
 * Every ending has a code. It is shown large and selectable, and the page
 * counts down and sends the participant to Prolific's completion URL. The
 * button is there too, for a browser that blocks the redirect; and the code
 * stays on screen whatever happens, because a dead-end page with no code is
 * the commonest support message a study gets. At stage 0 the code is set on
 * Prolific to ask for a return, and the page says so.
 *
 * A stage-0 ending stored before those codes redirected has none, and still
 * gets the old "return your submission" page.
 */

import { useEffect, useRef, useState } from 'react'
import type { StudyCompletePayload, StudyOutcome } from '@overcooked/shared'
import { PROLIFIC_HOME_URL } from '@overcooked/shared'

interface EndScreenProps {
  result: StudyCompletePayload
  countdownMs: number
  onRedirect(how: 'auto' | 'clicked'): void
}

const WHY: Record<StudyOutcome, string> = {
  completed: 'Thank you for taking part.',
  no_consent: 'You chose not to take part.',
  device_incompatible: "This study can't run on your device or connection.",
  voice_check_failed: "We couldn't hear your microphone.",
  idle: 'The study ended because you were inactive for too long.',
  unmatched: "We couldn't find you a partner in time.",
  dropped: 'The study ended early.',
}

export function EndScreen({ result, countdownMs, onRedirect }: EndScreenProps) {
  const [left, setLeft] = useState(countdownMs)
  const redirected = useRef(false)
  const go = (how: 'auto' | 'clicked'): void => {
    if (!result.redirectUrl) return
    if (!redirected.current) onRedirect(how)
    redirected.current = true
    window.location.href = result.redirectUrl
  }

  useEffect(() => {
    if (!result.prolificRedirect || !result.redirectUrl) return
    const startedAt = Date.now()
    const timer = setInterval(() => {
      const remaining = countdownMs - (Date.now() - startedAt)
      setLeft(Math.max(0, remaining))
      if (remaining <= 0) {
        clearInterval(timer)
        go('auto')
      }
    }, 250)
    return () => clearInterval(timer)
  }, [result, countdownMs])

  if (!result.prolificRedirect || !result.completionCode) {
    return (
      <section className="gate" data-testid="study-return">
        <p className="gate-step">Study ended</p>
        <h1>Please return your submission</h1>
        <p className="hint">{WHY[result.reason]}</p>
        <p>
          On Prolific, choose <b>“Stop without completing”</b> so someone else can take your place. You
          don't need a code.
        </p>
        <p className="hint">
          <a href={PROLIFIC_HOME_URL}>Go to Prolific</a>
        </p>
      </section>
    )
  }

  const finished = result.reason === 'completed'
  const toReturn = result.paymentStage === 0
  return (
    <section className="gate study-end" data-testid="study-end" data-return={toReturn ? 'yes' : undefined}>
      <p className="gate-step">{toReturn ? 'Study ended' : 'Thanks for your time'}</p>
      <h1>{finished ? 'Study complete!' : toReturn ? 'Please return your submission' : 'Progress saved'}</h1>
      <p className="hint">
        {WHY[result.reason]}
        {finished || toReturn ? '' : " You'll be paid for what you completed."}
      </p>
      {toReturn && (
        <p data-testid="study-return-note">
          On Prolific, please return your submission so someone else can take your place.
        </p>
      )}
      <p className="hint">Your completion code:</p>
      <code className="study-code" data-testid="study-code">
        {result.completionCode}
      </code>
      <p className="hint" role="status">
        Returning you to Prolific in {Math.ceil(left / 1000)} s…
      </p>
      <div className="gate-actions">
        <button type="button" className="consent-agree" onClick={() => go('clicked')} data-testid="study-return-to-prolific">
          Return to Prolific
        </button>
      </div>
    </section>
  )
}
